/**
 * 密码重置 - 第二步：邮件链接带 token，输入新密码
 *
 * 完成后 session_version bump → 所有旧 access / session 立即失效（逻辑不变）。
 * 皮肤移植 design/Novel-admin/Login.html 表单样式。
 */

import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { confirmPasswordReset } from '~/hooks/use-auth';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';

export default function PasswordResetConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 密码规则与后端 PasswordSchema 对齐：≥8 位 + 含字母 + 含数字；并要求二次确认一致
  const pwValid = password.length >= 8 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
  const pwMatch = password === confirmPassword;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!pwValid || !pwMatch) return;
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof AdminApiError) toast(err.message, { tone: 'neg' });
      else toast('重置失败', { tone: 'neg' });
    } finally {
      setSubmitting(false);
    }
  };

  // 重置 token 缺失/失效（邮件链接被截断或过期）→ 兜底引导重新申请，而非填完才报错
  if (!token) {
    return (
      <div className="step">
        <div style={{ marginBottom: 18 }}>
          <h2 className="form-title">重置链接无效</h2>
          <p className="form-sub">重置 token 缺失或链接已失效，请重新申请密码重置邮件</p>
        </div>
        <Link
          to="/auth/password-reset/request"
          className="btn-big"
          style={{ textDecoration: 'none', color: 'var(--bg)' }}
        >
          重新申请重置
        </Link>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Link
            to="/auth/login"
            style={{ color: 'var(--fg-muted)', textDecoration: 'none', fontSize: 12.5 }}
          >
            返回登录
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="step">
        <div className="bind-success">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          密码已重置，请用新密码重新登录
        </div>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Link
            to="/auth/login"
            style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 13 }}
          >
            前往登录
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="step">
      <div style={{ marginBottom: 24 }}>
        <h2 className="form-title">重置密码</h2>
        <p className="form-sub">设置新的登录密码，旧会话将立即失效</p>
      </div>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="field">
          <label className="field-label" htmlFor="reset-pw">
            新密码
          </label>
          <PasswordInput
            id="reset-pw"
            className="input-big"
            placeholder="至少 8 位，含字母+数字"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="reset-pw2">
            确认新密码
          </label>
          <PasswordInput
            id="reset-pw2"
            className="input-big"
            placeholder="再次输入新密码"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {confirmPassword && !pwMatch ? (
            <span style={{ fontSize: 11.5, color: 'var(--neg)' }}>两次输入的密码不一致</span>
          ) : null}
        </div>
        <button
          type="submit"
          className="btn-big"
          style={{ marginTop: 4 }}
          disabled={submitting || !pwValid || !pwMatch}
        >
          {submitting ? '重置中…' : '重置密码'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <Link
            to="/auth/login"
            style={{ color: 'var(--fg-muted)', textDecoration: 'none', fontSize: 12.5 }}
          >
            返回登录
          </Link>
        </div>
      </form>
    </div>
  );
}
